import { useId, useState, type KeyboardEvent, type MouseEvent, type ReactNode, type RefObject } from 'react';
import { ARTICLE_PICTURE_CAPTION_MAX, ARTICLE_PICTURE_SIZES, fill, Size, articlePictureMinWidthOf, articlePictureWidthOf, t, type ArticlePictureSize } from '@bms2/shared';
import { Pill } from './Pill';
import { TextInput } from './TextInput';
import type { PictureAlignment, PictureFacts } from './richTextPicture';
import { listRefusal, listStartOf, type ListAction, type ListFacts, type ListRefusal } from './richTextList';
import { tableRefusal, type TableRefusal } from './richTextTable';
import styles from './RichTextObjectBar.module.css';

/**
 * The article editor's control bar (piece 5; T397; decision 1240): one bar for
 * the one object the caret stands in, docked under the toolbar at every width.
 * 🔨 The bar is drawn from the facts the editor hands it and changes nothing
 * itself; every press goes back to the editor, which acts on the object's own
 * nodes. That is what lets the harness draw a bar with no surface beside it.
 */
export const richTextObjectBarDocumentedStates = ['table', 'tableAsk', 'tableLimits', 'tablePhone', 'picture', 'pictureOwnSize', 'picturePhone', 'list', 'listNested', 'listLimits', 'listPhone'] as const;

/** What the table bar shows: its size, whether its first row is a header row, and whether the caret stands in that row. */
export interface TableFacts {
  rows: number;
  columns: number;
  header: boolean;
  inHeader: boolean;
}

/** The table bar's presses (decisions 1240, 1241). */
export type TableAction = 'rowAbove' | 'rowBelow' | 'rowRemove' | 'columnLeft' | 'columnRight' | 'columnRemove' | 'header' | 'delete';

/** The words each refusal says, a sentence each (decision 1241). */
const REFUSAL_KEYS: Readonly<Record<TableRefusal, Parameters<typeof t>[0]>> = {
  maxRows: 'rte.bar.maxRows',
  maxColumns: 'rte.bar.maxColumns',
  lastRow: 'rte.bar.lastRow',
  lastColumn: 'rte.bar.lastColumn',
  aboveHeader: 'rte.bar.aboveHeader',
};

/** A count and its noun, one row or four rows. */
export function countWords(count: number, one: string, many: string): string {
  return fill(count === 1 ? one : many, { n: String(count) });
}

/** The table's size as the bar names it, 4 rows · 3 columns. */
export function tableSizeWords(facts: TableFacts, joiner: string): string {
  return [countWords(facts.rows, t('rte.bar.row'), t('rte.bar.rows')), countWords(facts.columns, t('rte.bar.column'), t('rte.bar.columns'))].join(joiner);
}

/** The question Delete Table asks, naming what goes with it. */
export function tableDeleteQuestion(facts: TableFacts): string {
  return fill(t('rte.bar.deleteAsk'), { size: tableSizeWords(facts, ' and ') });
}

/** What a screen reader hears once, when the caret first enters a table. */
export function tableAnnouncement(facts: TableFacts): string {
  return fill(t('rte.bar.announce.table'), { size: tableSizeWords(facts, ', ') });
}

/** A bar control takes the focus itself, or through the field it wraps: the percent box, the alt text. */
export function focusControl(control: HTMLElement | null | undefined): void {
  if (!control) return;
  const target = control.matches('button, select, input') ? control : control.querySelector<HTMLElement>('input, button, select');
  target?.focus();
}

/** The pointer presses a bar control without taking the focus from the text, so the caret stays in its cell. */
const keepFocus = (event: MouseEvent) => event.preventDefault();

interface BarButtonProps {
  label: string;
  control: string;
  onPress: () => void;
  /** Why the control cannot act here, or null where it can. */
  unavailable?: string | null;
  pressed?: boolean;
  /** A press that takes the words with it, drawn in the destructive colour. */
  destructive?: boolean;
}

/**
 * 🔨 A control that cannot act is marked unavailable and never disabled
 * (decision 1240): a disabled button drops out of the keyboard's reach, and
 * then its reason is read by nobody who needs it read (decision 0559).
 */
function BarButton({ label, control, onPress, unavailable = null, pressed, destructive = false }: BarButtonProps) {
  const reasonId = useId();
  return (
    <>
      <button
        type="button"
        className={destructive ? `${styles.control} ${styles.destructive}` : styles.control}
        onMouseDown={keepFocus}
        onClick={() => {
          if (unavailable === null) onPress();
        }}
        aria-disabled={unavailable !== null ? true : undefined}
        aria-pressed={pressed}
        aria-describedby={unavailable !== null ? reasonId : undefined}
        title={unavailable ?? undefined}
        data-bar-control={control}
        data-rich-text-tool={control}
        data-unavailable={unavailable !== null ? 'true' : 'false'}
      >
        {label}
      </button>
      {unavailable !== null ? (
        <span className={styles.reason} id={reasonId}>
          {unavailable}
        </span>
      ) : null}
    </>
  );
}

/** One entry of a phone menu: the press it makes, its words, and why it cannot act where it cannot. */
interface MenuEntry {
  action: string;
  label: string;
  unavailable?: string | null;
}

/**
 * A phone menu of the bar (decision 1240): the native picker, which a phone
 * draws as its own sheet, applies the entry chosen and resets to its name.
 */
function BarMenu({ label, control, entries, onChoose }: { label: string; control: string; entries: readonly MenuEntry[]; onChoose: (action: string) => void }) {
  return (
    <select
      className={styles.menu}
      aria-label={label}
      value=""
      onChange={(event) => {
        const chosen = event.target.value;
        event.target.value = '';
        if (chosen) onChoose(chosen);
      }}
      data-bar-control={control}
      data-rich-text-bar-menu={control}
    >
      <option value="" disabled>
        {label}
      </option>
      {entries.map((entry) => (
        <option key={entry.action} value={entry.action} disabled={(entry.unavailable ?? null) !== null} data-bar-entry={entry.action}>
          {entry.label}
        </option>
      ))}
    </select>
  );
}

interface BarShellProps {
  kind: 'table' | 'picture' | 'list';
  label: string;
  pill: string;
  facts?: string;
  barRef?: RefObject<HTMLDivElement> | undefined;
  onEscape: () => void;
  children: ReactNode;
}

/**
 * The bar itself, a labelled toolbar (decision 1240). ← and → move along its
 * controls and Home and End go to either end, as in the main toolbar; a text box
 * in the bar keeps those keys for its own caret. Esc hands the focus back to the
 * text, where the caret was.
 */
function BarShell({ kind, label, pill, facts, barRef, onEscape, children }: BarShellProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onEscape();
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const target = event.target as HTMLElement;
    if (target instanceof HTMLInputElement) return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-bar-control]'));
    const at = controls.findIndex((each) => each === target || each.contains(target));
    if (at < 0 || controls.length === 0) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? controls.length - 1 : event.key === 'ArrowLeft' ? Math.max(at - 1, 0) : Math.min(at + 1, controls.length - 1);
    focusControl(controls[next]);
  };
  return (
    <div ref={barRef} className={styles.bar} role="toolbar" aria-label={label} onKeyDown={onKeyDown} data-rich-text-bar={kind}>
      <span className={styles.name}>
        <Pill text={pill} foreground="var(--navy-primary)" background="var(--selection-bg)" size={Size.Small} />
        {facts ? <span className={styles.facts} data-rich-text-bar-facts="true">{facts}</span> : null}
      </span>
      {children}
    </div>
  );
}

export interface TableBarProps {
  facts: TableFacts;
  onAction: (action: TableAction) => void;
  onEscape: () => void;
  /** Delete Table's question is open in the bar's place (decision 1241). */
  asking?: boolean;
  onConfirmDelete?: () => void;
  onKeep?: () => void;
  /** Folded into three menus, Row, Column and Table, at a phone's width. */
  phone?: boolean;
  barRef?: RefObject<HTMLDivElement> | undefined;
}

/**
 * The table bar (decisions 1240, 1241): its size, then its row, column and
 * table controls, or the three menus on a phone. 🔨 Delete Table asks in the
 * bar's own place, with no dialog box, so nothing else on the page moves.
 */
export function TableBar({ facts, onAction, onEscape, asking = false, onConfirmDelete, onKeep, phone = false, barRef }: TableBarProps) {
  const refused = (action: Parameters<typeof tableRefusal>[0]): string | null => {
    const refusal = tableRefusal(action, facts);
    return refusal ? t(REFUSAL_KEYS[refusal]) : null;
  };
  const size = phone ? fill(t('rte.bar.phoneSize'), { rows: String(facts.rows), columns: String(facts.columns) }) : tableSizeWords(facts, ' · ');
  if (asking) {
    return (
      <BarShell kind="table" label={t('rte.bar.tableLabel')} pill={t('rte.bar.table')} barRef={barRef} onEscape={onKeep ?? onEscape}>
        <span className={styles.question} data-rich-text-bar-ask="delete">
          {tableDeleteQuestion(facts)}
        </span>
        <span className={styles.group}>
          <BarButton label={t('rte.bar.delete')} control="tableDeleteConfirm" onPress={() => onConfirmDelete?.()} destructive />
          <BarButton label={t('rte.bar.keep')} control="tableDeleteKeep" onPress={() => onKeep?.()} />
        </span>
      </BarShell>
    );
  }
  const rowEntries: MenuEntry[] = [
    { action: 'rowAbove', label: t('rte.bar.rowAbove'), unavailable: refused('rowAbove') },
    { action: 'rowBelow', label: t('rte.bar.rowBelow'), unavailable: refused('rowBelow') },
    { action: 'rowRemove', label: t('rte.bar.rowRemove'), unavailable: refused('rowRemove') },
  ];
  const columnEntries: MenuEntry[] = [
    { action: 'columnLeft', label: t('rte.bar.columnLeft'), unavailable: refused('columnLeft') },
    { action: 'columnRight', label: t('rte.bar.columnRight'), unavailable: refused('columnRight') },
    { action: 'columnRemove', label: t('rte.bar.columnRemove'), unavailable: refused('columnRemove') },
  ];
  const tableEntries: MenuEntry[] = [
    { action: 'header', label: facts.header ? t('rte.bar.headerOn') : t('rte.bar.header') },
    { action: 'delete', label: t('rte.bar.delete') },
  ];
  const choose = (action: string) => onAction(action as TableAction);
  return (
    <BarShell kind="table" label={t('rte.bar.tableLabel')} pill={t('rte.bar.table')} facts={size} barRef={barRef} onEscape={onEscape}>
      {phone ? (
        <span className={`${styles.group} ${styles.menus}`}>
          <BarMenu label={t('rte.bar.rowMenu')} control="rowMenu" entries={rowEntries} onChoose={choose} />
          <BarMenu label={t('rte.bar.columnMenu')} control="columnMenu" entries={columnEntries} onChoose={choose} />
          <BarMenu label={t('rte.bar.tableMenu')} control="tableMenu" entries={tableEntries} onChoose={choose} />
        </span>
      ) : (
        <>
          <span className={styles.group}>
            <BarButton label={t('rte.bar.rowAbove')} control="tableRowAbove" onPress={() => onAction('rowAbove')} unavailable={refused('rowAbove')} />
            <BarButton label={t('rte.bar.rowBelow')} control="tableRowBelow" onPress={() => onAction('rowBelow')} unavailable={refused('rowBelow')} />
            <BarButton label={t('rte.bar.rowRemove')} control="tableRowRemove" onPress={() => onAction('rowRemove')} unavailable={refused('rowRemove')} />
          </span>
          <span className={styles.group}>
            <BarButton label={t('rte.bar.columnLeft')} control="tableColumnLeft" onPress={() => onAction('columnLeft')} unavailable={refused('columnLeft')} />
            <BarButton label={t('rte.bar.columnRight')} control="tableColumnRight" onPress={() => onAction('columnRight')} unavailable={refused('columnRight')} />
            <BarButton label={t('rte.bar.columnRemove')} control="tableColumnRemove" onPress={() => onAction('columnRemove')} unavailable={refused('columnRemove')} />
          </span>
          <span className={styles.group}>
            <BarButton label={t('rte.bar.header')} control="tableHeader" onPress={() => onAction('header')} pressed={facts.header} />
            <BarButton label={t('rte.bar.delete')} control="tableDelete" onPress={() => onAction('delete')} destructive />
          </span>
        </>
      )}
    </BarShell>
  );
}

/** The four named sizes in the order the bar offers them, each with its words and its control's name (decision 1243). */
const PICTURE_SIZES: readonly { size: ArticlePictureSize; key: Parameters<typeof t>[0]; control: string; line: Parameters<typeof t>[0] }[] = [
  { size: 'small', key: 'rte.picture.small', control: 'pictureSmall', line: 'rte.picture.line.small' },
  { size: 'medium', key: 'rte.picture.medium', control: 'pictureMedium', line: 'rte.picture.line.medium' },
  { size: 'large', key: 'rte.picture.large', control: 'pictureLarge', line: 'rte.picture.line.large' },
  { size: 'full', key: 'rte.picture.full', control: 'pictureFull', line: 'rte.picture.line.full' },
];

const ALIGNMENTS: readonly { align: PictureAlignment; key: Parameters<typeof t>[0]; control: string; heard: Parameters<typeof t>[0] }[] = [
  { align: 'left', key: 'rte.picture.left', control: 'pictureLeft', heard: 'rte.picture.heard.left' },
  { align: 'center', key: 'rte.picture.centre', control: 'pictureCentre', heard: 'rte.picture.heard.centre' },
  { align: 'right', key: 'rte.picture.right', control: 'pictureRight', heard: 'rte.picture.heard.right' },
];

/** The named size a width is, or none: a typed percent lights the name it matches. */
function namedSize(width: number | null) {
  return PICTURE_SIZES.find((entry) => ARTICLE_PICTURE_SIZES[entry.size] === width) ?? null;
}

/** A picture's size in words: its name, its percent, or its own size. */
export function pictureSizeWords(width: number | null): string {
  const named = namedSize(width);
  if (named) return t(named.key);
  return width === null ? t('rte.picture.ownSize') : fill(t('rte.picture.percentOnly'), { n: String(width) });
}

/** What a screen reader hears once, when a picture is first selected. */
export function pictureAnnouncement(facts: PictureFacts): string {
  const align = ALIGNMENTS.find((entry) => entry.align === facts.align) ?? ALIGNMENTS[0]!;
  return fill(t('rte.bar.announce.picture'), { size: pictureSizeWords(facts.width), align: t(align.heard) });
}

/** The line under the size row: what the width means, or that the picture draws at its own size. */
function sizeLine(width: number | null): string {
  if (width === null) return t('rte.picture.line.own');
  const named = namedSize(width);
  const meaning = named ? t(named.line) : fill(t('rte.picture.line.percent'), { n: String(width) });
  return `${meaning} ${t('rte.picture.line.same')}`;
}

export interface PictureBarProps {
  facts: PictureFacts;
  /** A width the article keeps, 10 to 100; the bar never hands up one it would drop. */
  onSize: (percent: number) => void;
  onAlign: (align: PictureAlignment) => void;
  onAlt: (alt: string) => void;
  /** 🔒 The caption drawn under the picture (decision 1476); emptied, the picture keeps none. */
  onCaption: (caption: string) => void;
  /** 🔒 The desired minimum on screen, 100 to 1200 pixels, or none for the 200 default (decision 1942); the bar never hands up one it would drop. */
  onMinWidth?: (pixels: number | null) => void;
  onRemove: () => void;
  onEscape: () => void;
  phone?: boolean;
  barRef?: RefObject<HTMLDivElement> | undefined;
}

/**
 * The picture bar (T372; decision 1243): the size row, the alignment of the
 * picture's own line, Remove Picture, and the alt text and the caption (decision
 * 1476) across the width, with
 * one line under it saying what the width means. 🔨 No drag handles: every
 * width is a percent the reader and the PDF draw the same. 🔨 A typed percent
 * outside 10 to 100 is refused as it is typed, and the picture keeps its last
 * width.
 */
export function PictureBar({ facts, onSize, onAlign, onAlt, onCaption, onMinWidth, onRemove, onEscape, phone = false, barRef }: PictureBarProps) {
  const [typedMinimum, setTypedMinimum] = useState(facts.minWidth === null ? '' : String(facts.minWidth));
  const [minimumRefused, setMinimumRefused] = useState(false);
  // A minimum read from another picture is what the box shows next, as the width's box does.
  const [shownMinimum, setShownMinimum] = useState(facts.minWidth);
  if (shownMinimum !== facts.minWidth) {
    setShownMinimum(facts.minWidth);
    setTypedMinimum(facts.minWidth === null ? '' : String(facts.minWidth));
    setMinimumRefused(false);
  }
  /** 🔨 A minimum outside 100 to 1200 is refused as it is typed, and the picture keeps its last one; an emptied box clears it (decision 1942). */
  const typeMinimum = (value: string) => {
    setTypedMinimum(value);
    if (value.trim() === '') {
      setMinimumRefused(false);
      if (facts.minWidth !== null) onMinWidth?.(null);
      return;
    }
    const pixels = articlePictureMinWidthOf(value);
    setMinimumRefused(pixels === null);
    if (pixels !== null && pixels !== facts.minWidth) onMinWidth?.(pixels);
  };
  const [typed, setTyped] = useState(facts.width === null ? '' : String(facts.width));
  const [refused, setRefused] = useState(false);
  const [another, setAnother] = useState(false);
  // A width set from a named size, or read from another picture, is what the box shows next; read while drawing, as the width changes.
  const [shownWidth, setShownWidth] = useState(facts.width);
  if (shownWidth !== facts.width) {
    setShownWidth(facts.width);
    setTyped(facts.width === null ? '' : String(facts.width));
    setRefused(false);
  }
  const type = (value: string) => {
    setTyped(value);
    const percent = articlePictureWidthOf(value);
    setRefused(percent === null);
    if (percent !== null && percent !== facts.width) onSize(percent);
  };
  const named = namedSize(facts.width);
  const percentBox = (
    <span className={styles.percent} data-bar-control="picturePercent" data-rich-text-tool="picturePercent">
      <TextInput
        size={Size.Small}
        chars={4}
        label={t('rte.picture.percentLabel')}
        labelHidden
        value={typed}
        onChange={type}
        error={refused ? t('rte.picture.refused') : null}
        name="rtePicturePercent"
      />
      <span className={styles.facts}>{t('rte.picture.ofColumn')}</span>
    </span>
  );
  const sizeEntries: MenuEntry[] = [...PICTURE_SIZES.map((entry) => ({ action: entry.size, label: t(entry.key) })), { action: 'another', label: t('rte.picture.another') }];
  const menuLabel = named ? fill(t('rte.picture.menuNamed'), { name: t(named.key), n: String(facts.width) }) : facts.width === null ? t('rte.picture.menuOwn') : pictureSizeWords(facts.width);
  return (
    <BarShell kind="picture" label={t('rte.bar.pictureLabel')} pill={t('rte.bar.picture')} barRef={barRef} onEscape={onEscape}>
      <span className={styles.group} data-rich-text-bar-group="size">
        {phone && !another ? (
          <BarMenu
            label={menuLabel}
            control="pictureSizeMenu"
            entries={sizeEntries}
            onChoose={(choice) => {
              if (choice === 'another') setAnother(true);
              else onSize(ARTICLE_PICTURE_SIZES[choice as ArticlePictureSize]);
            }}
          />
        ) : null}
        {phone ? null : PICTURE_SIZES.map((entry) => (
          <BarButton key={entry.size} label={t(entry.key)} control={entry.control} onPress={() => onSize(ARTICLE_PICTURE_SIZES[entry.size])} pressed={named?.size === entry.size} />
        ))}
        {phone ? null : <span className={styles.facts}>{t('rte.picture.or')}</span>}
        {!phone || another ? percentBox : null}
      </span>
      {onMinWidth ? (
        <span className={styles.percent} data-bar-control="pictureMinWidth" data-rich-text-tool="pictureMinWidth">
          <TextInput
            size={Size.Small}
            chars={5}
            label={t('rte.picture.minWidthLabel')}
            value={typedMinimum}
            onChange={typeMinimum}
            error={minimumRefused ? t('rte.picture.minWidthRefused') : null}
            name="rtePictureMinWidth"
          />
          <span className={styles.facts}>{t('rte.picture.pixels')}</span>
        </span>
      ) : null}
      <span className={styles.group} data-rich-text-bar-group="align">
        {ALIGNMENTS.map((entry) => (
          <BarButton key={entry.align} label={t(entry.key)} control={entry.control} onPress={() => onAlign(entry.align)} pressed={facts.align === entry.align} />
        ))}
      </span>
      <span className={styles.group}>
        <BarButton label={t('rte.picture.remove')} control="pictureRemove" onPress={onRemove} destructive />
      </span>
      <span className={styles.wide} data-bar-control="pictureAlt" data-rich-text-tool="pictureAlt">
        <TextInput size={Size.Large} label={t('rte.picture.alt')} value={facts.alt} onChange={onAlt} name="rtePictureAlt" maxLength={ARTICLE_PICTURE_CAPTION_MAX} />
      </span>
      <span className={styles.wide} data-bar-control="pictureCaption" data-rich-text-tool="pictureCaption">
        <TextInput size={Size.Large} label={t('rte.picture.caption')} value={facts.caption} onChange={onCaption} name="rtePictureCaption" maxLength={ARTICLE_PICTURE_CAPTION_MAX} />
      </span>
      <span className={styles.line} data-rich-text-bar-line="size">
        {sizeLine(facts.width)}
        {facts.minWidth === null ? null : ` ${fill(t('rte.picture.line.minimum'), { n: String(facts.minWidth) })}`}
      </span>
      {facts.alt.trim() === '' ? (
        <span className={styles.line} data-rich-text-bar-line="alt">
          {t('rte.picture.noAlt')}
        </span>
      ) : null}
    </BarShell>
  );
}

/** The words each list refusal says, a sentence each (decision 1271). */
const LIST_REFUSAL_KEYS: Readonly<Record<ListRefusal, Parameters<typeof t>[0]>> = {
  firstItem: 'rte.list.firstItem',
  maxDepth: 'rte.list.maxDepth',
};

/** A list's number as the bar names it: a step in a numbered list, an item in a bulleted one. */
function stepWords(kind: ListFacts['kind'], n: number, step: Parameters<typeof t>[0], item: Parameters<typeof t>[0]): string {
  return fill(t(kind === 'numbered' ? step : item), { n: String(n) });
}

/** What the list bar names: its level, the item a nested list stands under, and its items (decision 1270). */
export function listFactsWords(facts: ListFacts): string {
  const parts = [fill(t('rte.list.level'), { n: String(facts.level) })];
  if (facts.level > 1 && facts.parent) parts.push(stepWords(facts.parent.kind, facts.parent.step, 'rte.list.underStep', 'rte.list.underItem'));
  parts.push(countWords(facts.items, t('rte.list.item'), t('rte.list.items')));
  return parts.join(' · ');
}

/** What a screen reader hears once, when the caret first enters a list. */
export function listAnnouncement(facts: ListFacts): string {
  return fill(t(facts.kind === 'numbered' ? 'rte.bar.announce.numbered' : 'rte.bar.announce.bulleted'), { level: String(facts.level) });
}

/** The line under the list bar: what Indent and Outdent do to this item, in its own numbers. */
export function listLine(facts: ListFacts): string {
  const step = facts.start + facts.position - 1;
  const lines: string[] = [];
  if (listRefusal(facts) === null) {
    lines.push(fill(t(facts.kind === 'numbered' ? 'rte.list.line.indentStep' : 'rte.list.line.indentItem'), { n: String(facts.kind === 'numbered' ? step : facts.position), m: String(facts.kind === 'numbered' ? step - 1 : facts.position - 1) }));
  }
  if (facts.level === 1) lines.push(t('rte.list.line.outdentOut'));
  else if (facts.parent) lines.push(stepWords(facts.parent.kind, facts.parent.step + 1, 'rte.list.line.outdentStep', 'rte.list.line.outdentItem'));
  else lines.push(t('rte.list.line.outdentBare'));
  return lines.join(' ');
}

export interface ListBarProps {
  facts: ListFacts;
  onAction: (action: ListAction) => void;
  /** A start number the list keeps, 1 to 999; the bar never hands up one it would drop. */
  onStart: (start: number) => void;
  onEscape: () => void;
  /** At a phone's width the bar wraps to two lines, the kind on the first and the nesting on the second. */
  phone?: boolean;
  barRef?: RefObject<HTMLDivElement> | undefined;
}

/**
 * The list bar (T397; decisions 1270, 1271): the list's kind and level, Bulleted
 * or Numbered for the whole list at the caret's level, Start At on a numbered
 * list, Indent and Outdent, and Remove List, with one line under it saying what
 * Indent and Outdent do to this item. 🔨 A typed start outside 1 to 999 is
 * refused as it is typed, and the list keeps its last number.
 */
export function ListBar({ facts, onAction, onStart, onEscape, phone = false, barRef }: ListBarProps) {
  const [typed, setTyped] = useState(String(facts.start));
  const [refused, setRefused] = useState(false);
  // A start set here, or read from another list, is what the box shows next; read while drawing, as the start changes.
  const [shownStart, setShownStart] = useState(facts.start);
  if (shownStart !== facts.start) {
    setShownStart(facts.start);
    setTyped(String(facts.start));
    setRefused(false);
  }
  const type = (value: string) => {
    setTyped(value);
    const start = listStartOf(value);
    setRefused(start === null);
    if (start !== null && start !== facts.start) onStart(start);
  };
  const refusal = listRefusal(facts);
  const numbered = facts.kind === 'numbered';
  return (
    <BarShell kind="list" label={t('rte.bar.listLabel')} pill={t(numbered ? 'rte.bar.listNumbered' : 'rte.bar.listBulleted')} facts={listFactsWords(facts)} barRef={barRef} onEscape={onEscape}>
      <span className={styles.group} data-rich-text-bar-group="kind">
        <BarButton label={t('rte.list.bulleted')} control="listBulleted" onPress={() => onAction('bulleted')} pressed={!numbered} />
        <BarButton label={t('rte.list.numbered')} control="listNumbered" onPress={() => onAction('numbered')} pressed={numbered} />
      </span>
      {numbered ? (
        <span className={`${styles.group} ${styles.percent}`} data-bar-control="listStart" data-rich-text-tool="listStart">
          <span className={styles.facts} aria-hidden="true">
            {t('rte.list.startAt')}
          </span>
          <TextInput size={Size.Small} chars={4} label={t('rte.list.startAt')} labelHidden value={typed} onChange={type} error={refused ? t('rte.list.startRefused') : null} name="rteListStart" />
        </span>
      ) : null}
      {phone ? <span className={styles.lineBreak} aria-hidden="true" /> : null}
      <span className={phone ? `${styles.group} ${styles.first}` : styles.group} data-rich-text-bar-group="nest">
        <BarButton label={t('rte.list.indent')} control="listIndent" onPress={() => onAction('indent')} unavailable={refusal ? t(LIST_REFUSAL_KEYS[refusal]) : null} />
        <BarButton label={t('rte.list.outdent')} control="listOutdent" onPress={() => onAction('outdent')} />
      </span>
      <span className={styles.group}>
        <BarButton label={t('rte.list.remove')} control="listRemove" onPress={() => onAction('remove')} />
      </span>
      <span className={styles.line} data-rich-text-bar-line="list">
        {listLine(facts)}
      </span>
    </BarShell>
  );
}
